import { Box, Grid2, Stack, Typography } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ContactForm from '@components/ContactForm/ContactForm';
import ContentLoader from '@components/ContentLoader';
import Icon from '@components/Icon/Icon';
import IconButton from '@components/IconButton';

import { useDeleteCareTeamMember } from '@hooks/api/care_team';
import { useDeleteRelatedPerson } from '@hooks/api/related-person';
import { useFeatureFlags } from '@hooks/useFeatureFlags';
import { useModal } from '@hooks/useModal';
import { useSession } from '@hooks/useSession';

import { HorizontalRule } from '@layouts/AuthLayout';

import type { PatientContactType } from '@models/contact';
import type { RelatedPersonRecord } from '@models/related-person';

import type { ContactModel } from 'generated/types';

import { FontWeight } from '../../../../theme/typography';
import ContactCard from './ContactCard';
import {
  compareCareTeamContacts,
  mapCareTeamContact,
  mapRelatedPerson,
} from './utils';

const messages = defineMessages({
  title: {
    id: 'contactForm.title',
    defaultMessage: '{action} {person}',
  },
  add: {
    id: 'GLOBAL.add',
    defaultMessage: 'Add',
  },
  edit: {
    id: 'GLOBAL.edit',
    defaultMessage: 'Edit',
  },
  relatedPerson: {
    id: 'contactForm.relatedPerson',
    defaultMessage: 'Related Person',
  },
  careTeam: {
    id: 'contactForm.careTeamContact',
    defaultMessage: 'Care Team Contact',
  },
});

interface ContactListProps {
  /** Contact type title */
  title: string;
  /** Is data in the process of being fetched? */
  loading: boolean;
  /** Careteam contacts */
  contacts?: ContactModel[];
  /** Related person contacts */
  relatedPersons?: RelatedPersonRecord[];
  /** Contact Type (related-person or care-team) */
  type?: PatientContactType;
  /* Pass in organization_ids for updates - Preserves important field */
  organization_ids?: string[];
  /* Is this contact list read only? (Shouldn't be able to C_UD Medly care team) */
  read_only?: boolean;
  /* Display title of contact list - defaulted to true */
  show_title?: boolean;
}

export default function ContactList({
  contacts = [],
  loading,
  title,
  type,
  relatedPersons = [],
  organization_ids,
  read_only,
  show_title = true,
}: ContactListProps) {
  const { formatMessage } = useIntl();
  const { flags } = useFeatureFlags();
  const { closeModal, openModal } = useModal();
  const {
    state: { activeProfile },
  } = useSession();
  const patient_id = activeProfile?.patient_id ?? '';
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const handleToggleExpand = (contactId: string) => {
    setExpandedId((prev) => (prev === contactId ? null : contactId));
  };

  const [deleteRelatedPerson, deleteRelatedPersonLoading] =
    useDeleteRelatedPerson();
  const [deleteCareTeamMember, deleteCareTeamMemberLoading] =
    useDeleteCareTeamMember();

  const canPost = read_only
    ? false
    : type === 'care-team'
      ? flags.isCareTeamUpdateCareTeamEnabled
      : type === 'related-person'
        ? flags.isRelatedPersonCreateRelatedPersonEnabled
        : false;
  const canUpdate = read_only
    ? false
    : type === 'care-team'
      ? flags.isCareTeamUpdateCareTeamEnabled
      : type === 'related-person'
        ? flags.isRelatedPersonUpdateRelatedPersonEnabled
        : false;
  const canDelete = read_only
    ? false
    : type === 'care-team'
      ? flags.isCareTeamDeleteCareTeamMemberEnabled
      : type === 'related-person'
        ? flags.isRelatedPersonDeleteRelatedPersonEnabled
        : false;

  const handleOpenModal = (
    contact?: ContactModel,
    person?: RelatedPersonRecord
  ) => {
    openModal({
      width: 1000,
      props: {
        height: '300px',
      },
      isPersistent: true,
      children: (
        <Box sx={{ p: 2 }}>
          <Stack direction={'row'} justifyContent="space-between">
            <Typography
              color="primary.main"
              variant="h6"
              sx={{ fontWeight: FontWeight.Light }}
            >
              {formatMessage(messages.title, {
                action: formatMessage(contact ? messages.edit : messages.add),
                person:
                  contact?.role_name ??
                  formatMessage(
                    type === 'care-team'
                      ? messages.careTeam
                      : messages.relatedPerson
                  ),
              })}
            </Typography>

            <Icon
              clickable
              color="text.secondary"
              name="xmark"
              onClick={closeModal}
              size="lg"
              type="fas"
            />
          </Stack>

          <HorizontalRule />
          <ContactForm
            contact={contact}
            contactType={type}
            onClose={closeModal}
            organization_ids={organization_ids}
            person={person}
          />
        </Box>
      ),
    });
  };

  const handleDeleteCareTeamContact = async (id: string) => {
    if (id) {
      await deleteCareTeamMember({
        patient_id,
        member: `#${id}`,
      });
    }
  };

  const handleDeleteRelatedPerson = async (id: string) => {
    if (id) {
      await deleteRelatedPerson({
        relatedPerson_id: id,
        patient_id,
      });
    }
  };

  const sortedContacts = useMemo(
    () => [...contacts].sort(compareCareTeamContacts),
    [contacts]
  );

  const addButton = canPost ? (
    <IconButton
      disabled={loading}
      color="text.primary"
      name="plus"
      onClick={() => {
        handleOpenModal();
      }}
      iconType="fas"
    />
  ) : null;

  return (
    <>
      <Stack alignItems="center" direction="row" spacing={2} mb={1.5}>
        {show_title && <Typography variant="h6">{title}</Typography>}
        {addButton}
      </Stack>

      <ContentLoader
        loading={
          loading || deleteCareTeamMemberLoading || deleteRelatedPersonLoading
        }
      >
        <Grid2 container spacing={3}>
          {sortedContacts.length > 0 &&
            sortedContacts.map((contact) => (
              <Grid2
                key={`contact-${contact.id}`}
                size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
              >
                <ContactCard
                  data={mapCareTeamContact(contact)}
                  expanded={expandedId === contact.id}
                  onToggle={() => handleToggleExpand(contact.id ?? '')}
                  onEdit={() => handleOpenModal(contact)}
                  onDelete={() => handleDeleteCareTeamContact(contact.id ?? '')}
                  canUpdate={!!canUpdate}
                  canDelete={!!canDelete}
                  loading={loading}
                />
              </Grid2>
            ))}

          {relatedPersons.length > 0 &&
            relatedPersons.map((person) => (
              <Grid2
                key={`related-person-${person.id}`}
                size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
              >
                <ContactCard
                  data={mapRelatedPerson(person)}
                  expanded={expandedId === person.id}
                  onToggle={() => handleToggleExpand(person.id ?? '')}
                  onEdit={() => handleOpenModal(undefined, person)}
                  onDelete={() => handleDeleteRelatedPerson(person.id ?? '')}
                  canUpdate={!!canUpdate}
                  canDelete={!!canDelete}
                  loading={loading}
                />
              </Grid2>
            ))}
        </Grid2>
      </ContentLoader>
    </>
  );
}
